import {
  MezzanineStorey,
  STOREY_MIN_HEIGHT,
  Shape,
  Storey,
} from '@shared/models/design/storey.model';
import { Point2D } from '@shared/models/geometry.model';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import {
  getStoreyBelow,
  getStoreyById,
  getStoreyIndex,
  getStoreys,
  getStoreysOwningSelector,
} from '@shared/helpers/design/storey.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  clamp,
  isCloseTo,
  roundToDecimals,
} from '@shared/helpers/math.helpers';
import { getDistance } from '@shared/helpers/geometry/coordinates.helpers';
import {
  getSegmentIntersectionPoint,
  isEqualPoints,
  isPointInPolygon,
  isPolygon2D,
} from '@shared/helpers/geometry/geometry.helpers';
import { designGuards } from '@shared/helpers/design/design-typeguards.helpers';
import {
  getCutoutShapes,
  getSolidShapes,
} from '@shared/helpers/design/cutout.helpers';

type ToggleMezzanineStoreyParams = {
  storey: Storey;
  storeys: Storey[];
  name?: string;
  mezzanineElevationDraft?: number;
};

type IsMezzanineAllowedParams = {
  /**
   * Storey whose mezzanine flag would change.
   */
  storey: Storey;
  /**
   * Physical storey directly below {@link storey}; used for parent height and parent cutouts.
   */
  storeyBelow: Storey | undefined;
  isGroundFloor: boolean;
  parentStoreyHeight: number;
  /**
   * Target value for {@link Storey.mezzanine} after a toggle.
   */
  nextMezzanine: boolean;
};

/**
 * Whether {@link Storey.mezzanine} may be set to {@link IsMezzanineAllowedParams.nextMezzanine}.
 * Turning mezzanine off is always allowed here; turning it on is blocked on the ground floor,
 * when the parent storey is too short, or when this storey or the one below has cutout shapes.
 * @param params
 * @returns true when the requested mezzanine state is allowed
 */
export const isMezzanineAllowed = ({
  storey,
  storeyBelow,
  isGroundFloor,
  parentStoreyHeight,
  nextMezzanine,
}: IsMezzanineAllowedParams): boolean => {
  if (!nextMezzanine) return true;
  if (isGroundFloor) return false;
  if (parentStoreyHeight < STOREY_MIN_HEIGHT * 2) return false;
  const parentHasCutouts = Boolean(
    storeyBelow && getCutoutShapes(storeyBelow).length > 0,
  );
  if (getCutoutShapes(storey).length > 0 || parentHasCutouts) return false;
  return true;
};

/** Persisted mezzanine elevation precision. Matches the validation tolerance of 0.001 m. */
const MEZZANINE_ELEVATION_DECIMALS = 3;

/** Shortest parent that can host a deck of {@link STOREY_MIN_HEIGHT}. */
const MIN_PARENT_HEIGHT_WITH_MEZZANINE = STOREY_MIN_HEIGHT * 2;

/**
 * Height and elevation of a mezzanine deck inside its parent storey.
 * {@link MezzanineDimensions.height} is exactly parent height minus {@link MezzanineDimensions.mezzanine_elevation}.
 */
export type MezzanineDimensions = {
  height: number;
  mezzanine_elevation: number;
};

/**
 * Elevation and height that keep a mezzanine inside its parent.
 * Height equals parent height minus elevation, stays at least {@link STOREY_MIN_HEIGHT},
 * and elevation stays strictly inside the parent. A requested elevation that would leave
 * a thinner deck is lowered so the pair still validates.
 * @param parentHeight Parent storey height in metres
 * @param requestedElevation Deck height above the parent floor, in metres
 * @param decimals Decimal places for the stored elevation
 * @returns undefined when the parent is not taller than {@link STOREY_MIN_HEIGHT}
 */
export const resolveMezzanineDimensions = (
  parentHeight: number,
  requestedElevation: number,
  decimals = MEZZANINE_ELEVATION_DECIMALS,
): MezzanineDimensions | undefined => {
  if (
    !guards.is.finiteNumber(parentHeight) ||
    !guards.is.finiteNumber(requestedElevation)
  ) {
    return undefined;
  }

  const maxElevation = parentHeight - STOREY_MIN_HEIGHT;
  if (!(maxElevation > 0)) return undefined;

  const minElevation = Math.min(0.001, maxElevation);
  const clamped = clamp(requestedElevation, minElevation, maxElevation);
  let elevation = roundToDecimals(clamped, decimals);
  // Rounding up can leave a deck under the minimum. Drop one stored step.
  // Flooring maxElevation + 1e-9 crosses that step for heights such as 4.1 m.
  if (parentHeight - elevation < STOREY_MIN_HEIGHT) {
    const step = 10 ** -decimals;
    elevation = roundToDecimals(elevation - step, decimals);
  }
  if (!(elevation > 0) || elevation >= parentHeight) return undefined;

  const height = parentHeight - elevation;
  if (height < STOREY_MIN_HEIGHT) return undefined;
  return { height, mezzanine_elevation: elevation };
};

/**
 * Get mezzanine or storey height.
 * Mezzanine height is the parent remainder after elevation, never a minimum that
 * disagrees with that remainder.
 * @param storey
 * @param parentStoreyHeight
 * @param mezzanineElevation
 * @param precision Decimal places used when rounding mezzanine elevation.
 * Defaults to the persisted elevation precision so a draft height matches the stored deck.
 * @returns
 */
export const getHeight = (
  storey: Storey,
  parentStoreyHeight?: number,
  mezzanineElevation?: number,
  precision = MEZZANINE_ELEVATION_DECIMALS,
): number => {
  if (!storey.mezzanine) return storey.height;
  if (!guards.is.finiteNumber(parentStoreyHeight) || parentStoreyHeight <= 0) {
    return 0;
  }
  const elevation = mezzanineElevation ?? parentStoreyHeight / 2;
  const resolved = resolveMezzanineDimensions(
    parentStoreyHeight,
    elevation,
    precision,
  );
  if (resolved) return resolved.height;
  // A short parent cannot host a deck. Keep the stored height so turning the
  // mezzanine off does not persist 0 and fail STOREY_HEIGHT_POSITIVE.
  if (!guards.is.finiteNumber(storey.height)) return 0;
  return Math.max(storey.height, STOREY_MIN_HEIGHT);
};

/**
 * Rewrite mezzanine storeys so height equals parent height minus elevation.
 * Already valid storeys are returned unchanged.
 * @param storeys Storeys of one building, bottom to top
 * @returns the same array when every mezzanine already satisfies the invariant
 */
export const applyMezzanineHeightInvariant = (storeys: Storey[]): Storey[] => {
  let changed = false;
  const next = storeys.map((storey) => {
    if (!designGuards.is.mezzanineStorey(storey)) return storey;
    const parent = getStoreyBelow(storeys, storey);
    if (!parent) return storey;
    const elevation = storey.mezzanine_elevation;
    if (!guards.is.finiteNumber(elevation)) return storey;

    const expectedHeight = parent.height - elevation;
    const inRange = elevation > 0 && elevation < parent.height;
    const matches = isCloseTo(storey.height, expectedHeight, 0.001);
    const tallEnough = storey.height >= STOREY_MIN_HEIGHT;
    if (inRange && matches && tallEnough) return storey;

    const resolved = resolveMezzanineDimensions(parent.height, elevation);
    if (!resolved) return storey;
    if (
      resolved.height === storey.height &&
      resolved.mezzanine_elevation === elevation
    ) {
      return storey;
    }
    changed = true;
    return {
      ...storey,
      height: resolved.height,
      mezzanine_elevation: resolved.mezzanine_elevation,
    };
  });
  return changed ? next : storeys;
};

/**
 * Keep a storey that hosts mezzanines at least {@link MIN_PARENT_HEIGHT_WITH_MEZZANINE}.
 * A shorter parent fails {@link isMezzanineAllowed} and enrich throws.
 * @param storeys Storeys of one building, bottom to top
 * @returns the same array when every parent is already tall enough
 */
export const raiseMezzanineParentHeights = (storeys: Storey[]): Storey[] => {
  let changed = false;
  const next = storeys.map((storey) => {
    if (designGuards.is.mezzanineStorey(storey)) return storey;
    if (getMezzanineStoreysInStorey(storeys, storey).length === 0)
      return storey;
    if (
      guards.is.finiteNumber(storey.height) &&
      storey.height >= MIN_PARENT_HEIGHT_WITH_MEZZANINE
    ) {
      return storey;
    }
    changed = true;
    return { ...storey, height: MIN_PARENT_HEIGHT_WITH_MEZZANINE };
  });
  return changed ? next : storeys;
};

/**
 * Whether a mezzanine deck cannot be given a height that matches its parent.
 * @param storeys Storeys of one building, bottom to top
 */
export const hasUnresolvedMezzanineStorey = (storeys: Storey[]): boolean =>
  storeys.some((storey) => {
    if (!designGuards.is.mezzanineStorey(storey)) return false;
    const parent = getStoreyBelow(storeys, storey);
    if (!parent || !guards.is.finiteNumber(storey.mezzanine_elevation)) {
      return true;
    }
    return !resolveMezzanineDimensions(
      parent.height,
      storey.mezzanine_elevation,
    );
  });

/**
 * Keep mezzanine height and elevation consistent after a storey patch.
 * An explicit mezzanine height, without a new elevation, is the deck thickness:
 * elevation becomes parent height minus that height. Every other change keeps
 * elevation and derives height, lowering elevation when the deck would be
 * thinner than {@link STOREY_MIN_HEIGHT}.
 * @param storeys Storeys after the patch has been applied
 * @param updates Patches that were applied, used to see which field was edited
 * @returns the same array when no mezzanine value changes
 */
export const syncMezzanineStoreysAfterUpdate = (
  storeys: Storey[],
  updates: readonly {
    id: string;
    height?: number;
    mezzanine_elevation?: number;
  }[],
): Storey[] => {
  let changed = false;
  const withExplicitHeight = storeys.map((storey) => {
    if (!designGuards.is.mezzanineStorey(storey)) return storey;
    const update = updates.find((item) => item.id === storey.id);
    if (!update || update.mezzanine_elevation !== undefined) return storey;
    if (!guards.is.finiteNumber(update.height)) return storey;

    const parent = getStoreyBelow(storeys, storey);
    if (!parent) return storey;
    const resolved = resolveMezzanineDimensions(
      parent.height,
      parent.height - update.height,
    );
    if (!resolved) return storey;
    if (
      resolved.height === storey.height &&
      resolved.mezzanine_elevation === storey.mezzanine_elevation
    ) {
      return storey;
    }
    changed = true;
    return {
      ...storey,
      height: resolved.height,
      mezzanine_elevation: resolved.mezzanine_elevation,
    };
  });
  const base = changed ? withExplicitHeight : storeys;
  return applyMezzanineHeightInvariant(base);
};

/**
 * Apply {@link applyMezzanineHeightInvariant} to every building.
 * @param design Building design
 * @returns the same design when no mezzanine needed a rewrite
 */
export const syncMezzanineDimensionsInDesign = (
  design: BuildingDesign,
): BuildingDesign => {
  let changed = false;
  const buildings = design.buildings.map((building) => {
    if (!guards.is.array(building.storeys)) return building;
    const storeys = applyMezzanineHeightInvariant(building.storeys);
    if (storeys === building.storeys) return building;
    changed = true;
    return { ...building, storeys };
  });
  return changed ? { ...design, buildings } : design;
};

/**
 * Picks a sensible mezzanine elevation fallback when none is provided.
 * @param parentStoreyHeight
 * @returns fallback elevation or undefined
 */
export const getMezzanineElevationFallback = (
  parentStoreyHeight?: number,
): number | undefined => {
  if (!parentStoreyHeight) return undefined;
  return roundToDecimals(parentStoreyHeight / 2, 3);
};

/**
 * Ensures derived mezzanine height is always positive before use.
 * @param storey
 * @param parentStoreyHeight
 * @param mezzanineElevation
 * @returns height or undefined
 */
export const getDerivedMezzanineHeight = (
  storey: Storey,
  parentStoreyHeight?: number,
  mezzanineElevation?: number,
): number | undefined => {
  const derived = getHeight(storey, parentStoreyHeight, mezzanineElevation, 3);
  return derived > 0 ? derived : undefined;
};

/**
 * Get all mezzanine storeys in a storey.
 * @param root
 * @param storey
 * @returns
 */
export const getMezzanineStoreysInStorey = (
  root: BuildingDesign | Storey[],
  storey: Storey,
): MezzanineStorey[] => {
  if (designGuards.is.mezzanineStorey(storey)) {
    return [];
  }
  const storeys = getStoreysOwningSelector(root, storey);
  const storeyIndex = getStoreyIndex(storeys, storey);
  const mezzanineStoreys: MezzanineStorey[] = [];

  // Add all storeys above the selected storey that are mezzanine (until first non-mezzanine storey).
  for (let index = storeyIndex + 1; index < storeys.length; index += 1) {
    const candidate = storeys[index];
    if (!designGuards.is.mezzanineStorey(candidate)) break; // Stop at first non-mezzanine storey.
    mezzanineStoreys.push(candidate);
  }
  return mezzanineStoreys;
};

/**
 * Get the parent of a mezzanine storey by finding the nearest non-mezzanine below it.
 * @param root
 * @param indexOrMezzanine
 * @returns
 */
export const getMezzanineParent = (
  root: BuildingDesign | Storey[],
  indexOrMezzanine: number | Storey,
): Storey | undefined => {
  const storeys = guards.is.number(indexOrMezzanine)
    ? getStoreys(root)
    : getStoreysOwningSelector(root, indexOrMezzanine);
  const mezzanineStorey = guards.is.number(indexOrMezzanine)
    ? storeys[indexOrMezzanine]
    : indexOrMezzanine;
  if (!mezzanineStorey) return undefined;
  // Make sure provided index or storey is a mezzanine (else there is no parent).
  if (!designGuards.is.mezzanineStorey(mezzanineStorey)) {
    return undefined;
  }
  return getStoreyBelow(storeys, mezzanineStorey);
};

/**
 * Clamp all mezzanine storeys inside a non-mezzanine storey.
 * @param storeys
 * @param parentStorey
 * @returns storeys with clamped mezzanine shapes
 */
export const clampMezzanineStoreysInStorey = (
  storeys: Storey[],
  parentStorey: Storey,
  options?: {
    removeNearbyClampedPoints?: boolean;
  },
): Storey[] => {
  if (designGuards.is.mezzanineStorey(parentStorey)) return storeys;
  const mezzanineStoreys = getMezzanineStoreysInStorey(storeys, parentStorey);
  if (!mezzanineStoreys.length) return storeys;

  // Only touch mezzanines directly stacked on this parent storey.
  const mezzanineIds = new Set(mezzanineStoreys.map((storey) => storey.id));
  let mutated = false;

  const nextStoreys = storeys.map((storey) => {
    if (!mezzanineIds.has(storey.id)) return storey;
    const clampedShapes = clampStoreyShapesToParent(
      storey,
      parentStorey,
      options,
    );
    if (!clampedShapes) return storey;
    if (areStoreyShapesEqual(storey.shapes, clampedShapes)) return storey;
    mutated = true;
    return { ...storey, shapes: clampedShapes };
  });

  return mutated ? nextStoreys : storeys;
};

/**
 * Insert boundary intersection points into mezzanine shapes whose edges
 * cross outside the parent storey. Vertices are assumed to already be
 * inside the parent (run {@link clampMezzanineStoreysInStorey} first).
 *
 * For each mezzanine edge that exits the parent polygon, the intersection
 * points with the parent boundary are computed and spliced into the shape
 * so the resulting polygon stays fully contained.
 * @param storeys All storeys in the design
 * @returns storeys with repaired mezzanine shapes
 */
export const insertMezzanineBoundaryPoints = (storeys: Storey[]): Storey[] => {
  let mutated = false;

  const nextStoreys = storeys.map((storey) => {
    if (!designGuards.is.mezzanineStorey(storey)) return storey;
    const parentStorey = getStoreyBelow(storeys, storey);
    if (!parentStorey) return storey;

    const repairedShapes = storey.shapes.map((shape) =>
      insertBoundaryPointsInShape(shape, parentStorey),
    );

    const changed = repairedShapes.some((s, i) => s !== storey.shapes[i]);
    if (!changed) return storey;
    mutated = true;
    return { ...storey, shapes: repairedShapes };
  });

  return mutated ? nextStoreys : storeys;
};

/**
 * For a single mezzanine shape, check each edge and insert parent boundary
 * points where the edge exits the parent.
 */
const insertBoundaryPointsInShape = (
  shape: Shape,
  parentStorey: Storey,
): Shape => {
  if (shape.points.length < 3) return shape;

  const result: Point2D[] = [];
  let inserted = false;

  for (let i = 0; i < shape.points.length; i++) {
    const start = shape.points[i];
    const end = shape.points[(i + 1) % shape.points.length];
    if (!start || !end) continue;

    result.push(start);

    if (isEdgeInsideParent(start, end, parentStorey)) continue;

    const missing = findMissingBoundaryPoints(start, end, parentStorey);
    if (missing.length > 0) {
      result.push(...missing);
      inserted = true;
    }
  }

  if (!inserted) return shape;
  const unique = removeNearbyPoints(result, 0.5);
  if (!isPolygon2D(unique)) return shape;
  return { ...shape, points: unique };
};

/**
 * Check if an edge is fully contained inside the parent storey shapes
 * by sampling along the edge. Uses the same fractions as the validation
 * in {@link isShapeInsideParentStoreyShapes}.
 */
const isEdgeInsideParent = (
  start: Point2D,
  end: Point2D,
  parentStorey: Storey,
): boolean => {
  const fractions = [0.25, 0.5, 0.75] as const;
  return fractions.every((t) => {
    const sample: Point2D = [
      start[0] + (end[0] - start[0]) * t,
      start[1] + (end[1] - start[1]) * t,
    ];
    return isPointInsideStoreyShapes(sample, parentStorey);
  });
};

/**
 * Compute the parametric projection of a point onto a directed segment.
 * Returns a value in (-∞, +∞) where [0, 1] means the projection falls
 * on the segment interior.
 */
const projectOntoEdge = (
  point: Point2D,
  start: Point2D,
  dx: number,
  dy: number,
  lenSq: number,
): number => ((point[0] - start[0]) * dx + (point[1] - start[1]) * dy) / lenSq;

/**
 * Find parent boundary points that should be inserted into a mezzanine edge
 * that exits the parent. Handles two cases:
 *
 * 1. **Skipped vertices** – The mezzanine edge endpoints sit on the parent
 *    boundary but the straight line between them bypasses a concave parent
 *    vertex. That vertex is projected onto the edge and inserted.
 *
 * 2. **Edge crossings** – The mezzanine edge genuinely crosses a parent
 *    boundary segment. The intersection point is computed and inserted.
 *
 * Returns points sorted by parametric distance from start.
 */
const findMissingBoundaryPoints = (
  start: Point2D,
  end: Point2D,
  parentStorey: Storey,
): Point2D[] => {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return [];

  const candidates: { point: Point2D; t: number }[] = [];

  for (const parentShape of parentStorey.shapes) {
    if (!isPolygon2D(parentShape.points)) continue;

    // Case 1: parent vertices that project onto the interior of the edge
    // and are close enough to be geometrically relevant.
    for (const vertex of parentShape.points) {
      const t = projectOntoEdge(vertex, start, dx, dy, lenSq);
      if (t <= 0.001 || t >= 0.999) continue;

      const projX = start[0] + t * dx;
      const projY = start[1] + t * dy;
      const distSq =
        (vertex[0] - projX) * (vertex[0] - projX) +
        (vertex[1] - projY) * (vertex[1] - projY);

      if (distSq > lenSq) continue;
      candidates.push({ point: vertex, t });
    }

    // Case 2: segment-segment intersections with the parent boundary.
    for (let j = 0; j < parentShape.points.length; j++) {
      const edgeStart = parentShape.points[j];
      const edgeEnd = parentShape.points[(j + 1) % parentShape.points.length];
      if (!edgeStart || !edgeEnd) continue;

      const ip = getSegmentIntersectionPoint(start, end, edgeStart, edgeEnd);
      if (!ip) continue;

      const t = projectOntoEdge(ip, start, dx, dy, lenSq);
      if (t <= 0.001 || t >= 0.999) continue;
      candidates.push({ point: ip, t });
    }
  }

  return candidates.sort((a, b) => a.t - b.t).map(({ point }) => point);
};

/**
 * Builds the storey payload for mezzanine updates with clamped shapes.
 * @param params
 * @returns next storey or undefined when validation fails
 */
export const toggleMezzanineStorey = ({
  storey,
  storeys,
  name,
  mezzanineElevationDraft,
}: ToggleMezzanineStoreyParams): Storey | undefined => {
  const parentStorey = getStoreyBelow(storeys, storey);
  const parentStoreyHeight = parentStorey?.height;
  if (!guards.is.number(parentStoreyHeight)) return undefined;

  const elevation = guards.is.number(mezzanineElevationDraft)
    ? mezzanineElevationDraft
    : getMezzanineElevationFallback(parentStoreyHeight);
  if (!guards.is.number(elevation)) return undefined;
  if (elevation <= 0 || elevation >= parentStoreyHeight) return undefined;

  // Clamp a too-thin deck so height stays parent height minus elevation.
  const resolved = resolveMezzanineDimensions(parentStoreyHeight, elevation);
  if (!resolved) return undefined;
  if (!parentStorey) return undefined;

  // Mezzanine footprints are solids only; cutouts must not be clamped as separate rings.
  const storeySolids: Storey = { ...storey, shapes: getSolidShapes(storey) };
  const clampedShapes = clampStoreyShapesToParent(storeySolids, parentStorey);
  if (!clampedShapes) return undefined;

  return {
    ...storey,
    name,
    height: resolved.height,
    mezzanine: true,
    mezzanine_elevation: resolved.mezzanine_elevation,
    elevation: undefined,
    shapes: clampedShapes,
  };
};

type LineLinkedPoint = {
  storeyId: Storey['id'];
  shapeId: Shape['id'];
  pointIndex: number;
  position: Point2D;
};

type MezzanineLineLinkedPoint = LineLinkedPoint;

type MezzanineLineLinkedPointsParams = {
  baseStoreys: Storey[];
  storeyId: Storey['id'];
  shapeId: Shape['id'];
  pointIndex: number;
  nextPosition: Point2D;
  tolerance?: number;
};

type LineLinkedPointsParams = {
  baseStoreys: Storey[];
  storeyId: Storey['id'];
  shapeId: Shape['id'];
  pointIndex: number;
  nextPosition: Point2D;
  tolerance?: number;
};

/**
 * Resolve points on storeys above that lie on the moved line segment.
 * @param params
 * @returns linked point updates
 */
export const getLineLinkedPointsAboveStorey = ({
  baseStoreys,
  storeyId,
  shapeId,
  pointIndex,
  nextPosition,
  tolerance = 0.001,
}: LineLinkedPointsParams): LineLinkedPoint[] => {
  const baseStorey = getStoreyById(baseStoreys, storeyId);
  if (!baseStorey) return [];
  const baseShape = baseStorey.shapes.find((shape) => shape.id === shapeId);
  if (!baseShape?.points?.length) return [];
  const baseIndex = getStoreyIndex(baseStoreys, baseStorey);
  if (baseIndex < 0) return [];
  let storeysAbove = baseStoreys.slice(baseIndex + 1);
  if (!storeysAbove.length) return [];

  // When moving a point on a mezzanine, only affect points on mezzanine storeys above.
  if (designGuards.is.mezzanineStorey(baseStorey)) {
    storeysAbove = storeysAbove.filter((s) =>
      designGuards.is.mezzanineStorey(s),
    );
    if (!storeysAbove.length) return [];
  }

  return getLineLinkedPointsInStoreys({
    storeys: storeysAbove,
    baseShape,
    pointIndex,
    nextPosition,
    tolerance,
  });
};

/**
 * Resolve line-linked points on upper storeys when multiple points move simultaneously.
 * For each segment of the base shape, determines whether one or both endpoints are moving
 * and projects upper-storey points onto the resulting new segment position.
 * @param params - base storeys, storey/shape IDs, moved point indices and their offset
 * @returns linked point updates for upper storeys
 */
export const getMultiPointLineLinkedPointsAboveStorey = ({
  baseStoreys,
  storeyId,
  shapeId,
  movedIndices,
  offset,
  movedPositions,
  tolerance = 0.001,
}: {
  baseStoreys: Storey[];
  storeyId: Storey['id'];
  shapeId: Shape['id'];
  movedIndices: Set<number>;
  offset: Point2D;
  movedPositions?: ReadonlyMap<number, Point2D>;
  tolerance?: number;
}): LineLinkedPoint[] => {
  const baseStorey = getStoreyById(baseStoreys, storeyId);
  if (!baseStorey) return [];
  const baseShape = baseStorey.shapes.find((shape) => shape.id === shapeId);
  if (!baseShape?.points?.length || baseShape.points.length < 2) return [];
  const baseIndex = getStoreyIndex(baseStoreys, baseStorey);
  if (baseIndex < 0) return [];
  let storeysAbove = baseStoreys.slice(baseIndex + 1);
  if (!storeysAbove.length) return [];

  if (designGuards.is.mezzanineStorey(baseStorey)) {
    storeysAbove = storeysAbove.filter((s) =>
      designGuards.is.mezzanineStorey(s),
    );
    if (!storeysAbove.length) return [];
  }

  return getMultiPointLineLinkedPointsInStoreys({
    storeys: storeysAbove,
    baseShape,
    movedIndices,
    offset,
    movedPositions,
    tolerance,
  });
};

/**
 * Get mezzanine points that lie on a moved parent storey line segment.
 * @param params
 * @returns linked mezzanine point updates
 */
export const getMezzanineLineLinkedPoints = ({
  baseStoreys,
  storeyId,
  shapeId,
  pointIndex,
  nextPosition,
  tolerance = 0.001,
}: MezzanineLineLinkedPointsParams): MezzanineLineLinkedPoint[] => {
  const baseStorey = getStoreyById(baseStoreys, storeyId);
  if (!baseStorey || designGuards.is.mezzanineStorey(baseStorey)) return [];
  const mezzanineStoreys = getMezzanineStoreysInStorey(baseStoreys, baseStorey);
  if (!mezzanineStoreys.length) return [];
  const baseShape = baseStorey.shapes.find((shape) => shape.id === shapeId);
  if (!baseShape?.points?.length) return [];
  return getLineLinkedPointsInStoreys({
    storeys: mezzanineStoreys,
    baseShape,
    pointIndex,
    nextPosition,
    tolerance,
  });
};

/**
 * Compute line-linked point updates within a storey collection.
 * @param params
 * @returns linked point updates
 */
function getLineLinkedPointsInStoreys({
  storeys,
  baseShape,
  pointIndex,
  nextPosition,
  tolerance,
}: {
  storeys: Storey[];
  baseShape: Shape;
  pointIndex: number;
  nextPosition: Point2D;
  tolerance: number;
}): LineLinkedPoint[] {
  if (baseShape.points.length < 2) return [];
  const movingPoint = baseShape.points[pointIndex];
  if (!movingPoint) return [];
  const prevPoint =
    baseShape.points[
      (pointIndex - 1 + baseShape.points.length) % baseShape.points.length
    ];
  const nextPoint =
    baseShape.points[(pointIndex + 1) % baseShape.points.length];
  if (!prevPoint || !nextPoint) return [];

  const updates: LineLinkedPoint[] = [];
  const seen = new Set<string>();

  storeys.forEach((storey) => {
    storey.shapes.forEach((shape) => {
      shape.points.forEach((point, index) => {
        const prevMatch = projectPointToSegment(point, prevPoint, movingPoint);
        const nextMatch = projectPointToSegment(point, movingPoint, nextPoint);

        // Prefer the closest segment so shared corners move predictably.
        const match = pickClosestSegmentMatch(prevMatch, nextMatch, tolerance);
        if (!match) return;

        const newSegment: [Point2D, Point2D] =
          match.segment === 'prev'
            ? [prevPoint, nextPosition]
            : [nextPosition, nextPoint];
        const position = interpolatePointOnSegment(
          newSegment[0],
          newSegment[1],
          match.t,
        );
        const key = `${storey.id}:${shape.id}:${index}`;
        if (seen.has(key)) return;
        seen.add(key);
        updates.push({
          storeyId: storey.id,
          shapeId: shape.id,
          pointIndex: index,
          position,
        });
      });
    });
  });

  return updates;
}

/**
 * Compute line-linked point updates when multiple base shape points move by the same offset.
 * Iterates each segment of the base shape and projects upper-storey points onto the new segment.
 */
function getMultiPointLineLinkedPointsInStoreys({
  storeys,
  baseShape,
  movedIndices,
  offset,
  movedPositions,
  tolerance,
}: {
  storeys: Storey[];
  baseShape: Shape;
  movedIndices: Set<number>;
  offset: Point2D;
  movedPositions?: ReadonlyMap<number, Point2D>;
  tolerance: number;
}): LineLinkedPoint[] {
  const points = baseShape.points;
  if (points.length < 2) return [];

  const [dx, dy] = offset;
  const updates: LineLinkedPoint[] = [];
  const seen = new Set<string>();

  // Check each segment (edge) of the polygon
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    const iMoved = movedIndices.has(i);
    const jMoved = movedIndices.has(j);
    // Skip segments where neither endpoint moves
    if (!iMoved && !jMoved) continue;

    const oldStart = points[i];
    const oldEnd = points[j];
    if (!oldStart || !oldEnd) continue;

    const newStart: Point2D = iMoved
      ? (movedPositions?.get(i) ?? [oldStart[0] + dx, oldStart[1] + dy])
      : oldStart;
    const newEnd: Point2D = jMoved
      ? (movedPositions?.get(j) ?? [oldEnd[0] + dx, oldEnd[1] + dy])
      : oldEnd;

    storeys.forEach((storey) => {
      storey.shapes.forEach((shape) => {
        shape.points.forEach((point, index) => {
          const key = `${storey.id}:${shape.id}:${index}`;
          if (seen.has(key)) return;

          const match = projectPointToSegment(point, oldStart, oldEnd);
          if (!match || match.distance > tolerance) return;

          // Project onto new segment at same parametric position
          const position = interpolatePointOnSegment(newStart, newEnd, match.t);
          seen.add(key);
          updates.push({
            storeyId: storey.id,
            shapeId: shape.id,
            pointIndex: index,
            position,
          });
        });
      });
    });
  }

  return updates;
}

/**
 * Clamp all mezzanine shape points to be inside the parent storey's shapes.
 * @param storey
 * @param parentStorey
 * @returns clamped shapes or undefined if invalid
 */
const clampStoreyShapesToParent = (
  storey: Storey,
  parentStorey: Storey,
  options?: {
    removeNearbyClampedPoints?: boolean;
  },
): Storey['shapes'] | undefined => {
  if (!guards.is.array(storey.shapes)) return undefined;
  if (!guards.is.array(parentStorey.shapes) || !parentStorey.shapes.length)
    return undefined;

  const clampedShapes = storey.shapes.reduce<Storey['shapes']>((acc, shape) => {
    const clampedShape = clampShapePointsToParent(shape, parentStorey, options);
    if (!clampedShape) return acc;
    acc.push(clampedShape);
    return acc;
  }, []);

  return clampedShapes.length ? clampedShapes : undefined;
};

/**
 * Clamp a single shape to the parent storey's shapes.
 * @param shape
 * @param parentStorey
 * @returns clamped shape or undefined if invalid
 */
const clampShapePointsToParent = (
  shape: Storey['shapes'][number],
  parentStorey: Storey,
  options?: {
    removeNearbyClampedPoints?: boolean;
  },
): Storey['shapes'][number] | undefined => {
  if (!guards.is.array(shape.points) || shape.points.length < 3)
    return undefined;

  const clampedPoints = shape.points.reduce<Point2D[]>((acc, point) => {
    const clamped = clampPointToParentStorey(point, parentStorey);
    if (!clamped) return acc;
    acc.push(clamped);
    return acc;
  }, []);

  const removeNearbyClampedPoints = options?.removeNearbyClampedPoints ?? true;
  // Keep drag-preview geometry stable; defer duplicate cleanup to commit when disabled.
  const normalizedPoints = removeNearbyClampedPoints
    ? removeNearbyPoints(clampedPoints, 0.5)
    : clampedPoints;
  if (!isPolygon2D(normalizedPoints)) return undefined;

  return {
    ...shape,
    points: normalizedPoints,
  };
};

/**
 * Clamp a point to the parent storey's shapes if outside.
 * @param point
 * @param parentStorey
 * @returns clamped point or undefined if no boundary available
 */
const clampPointToParentStorey = (
  point: Point2D,
  parentStorey: Storey,
): Point2D | undefined => {
  if (isPointInsideStoreyShapes(point, parentStorey)) return point;
  return getClosestPointOnShapesBoundary(point, parentStorey.shapes);
};

/**
 * Check if a point is inside any shape in the provided storey.
 * @param point
 * @param storey
 * @returns true when the point is inside a storey shape
 */
const isPointInsideStoreyShapes = (
  point: Point2D,
  storey: Storey | undefined,
): boolean => {
  if (!storey?.shapes?.length) return false;
  return storey.shapes.some((shape) => {
    if (!isPolygon2D(shape.points)) return false;
    return isPointInPolygon(point, shape.points);
  });
};

/**
 * Remove points that are within a given distance of another point.
 * @param points
 * @param minDistance
 * @returns filtered points
 */
const removeNearbyPoints = (
  points: Point2D[],
  minDistance: number,
): Point2D[] => {
  return points.reduce<Point2D[]>((acc, point) => {
    const hasNearby = acc.some(
      (existing) => getDistance(existing, point) <= minDistance,
    );
    if (hasNearby) return acc;
    acc.push(point);
    return acc;
  }, []);
};

/**
 * Find the closest point on any parent storey shape boundary.
 * @param point
 * @param shapes
 * @returns closest boundary point or undefined
 */
const getClosestPointOnShapesBoundary = (
  point: Point2D,
  shapes: Storey['shapes'],
): Point2D | undefined => {
  if (!guards.is.array(shapes) || !shapes.length) return undefined;

  return shapes.reduce<Point2D | undefined>((closest, shape) => {
    const candidate = getClosestPointOnShapeBoundary(point, shape);
    if (!candidate) return closest;
    if (!closest) return candidate;
    return getDistance(candidate, point) < getDistance(closest, point)
      ? candidate
      : closest;
  }, undefined);
};

/**
 * Find the closest point on a single shape boundary.
 * @param point
 * @param shape
 * @returns closest boundary point or undefined
 */
const getClosestPointOnShapeBoundary = (
  point: Point2D,
  shape: Storey['shapes'][number],
): Point2D | undefined => {
  if (!guards.is.array(shape.points) || shape.points.length < 2)
    return undefined;

  let closest: Point2D | undefined;
  for (let i = 0; i < shape.points.length; i += 1) {
    const start = shape.points[i];
    const end = shape.points[(i + 1) % shape.points.length];
    if (!start || !end) continue;
    const candidate = getClosestPointOnSegment(point, start, end);
    if (!closest) {
      closest = candidate;
      continue;
    }
    if (getDistance(candidate, point) < getDistance(closest, point)) {
      closest = candidate;
    }
  }
  return closest;
};

/**
 * Get the closest point on a line segment to the given point.
 * @param point
 * @param start
 * @param end
 * @returns closest point on segment
 */
const getClosestPointOnSegment = (
  point: Point2D,
  start: Point2D,
  end: Point2D,
): Point2D => {
  const [px, py] = point;
  const [x1, y1] = start;
  const [x2, y2] = end;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) return start;

  // Project point onto the segment and clamp to [0, 1].
  const t = clamp(((px - x1) * dx + (py - y1) * dy) / lengthSq, 0, 1);
  return [x1 + t * dx, y1 + t * dy];
};

type SegmentMatch = {
  t: number;
  distance: number;
  segment: 'prev' | 'next';
};

/**
 * Project a point onto a segment and return distance data.
 * @param point
 * @param start
 * @param end
 * @returns projection details or undefined when the segment is degenerate
 */
const projectPointToSegment = (
  point: Point2D,
  start: Point2D,
  end: Point2D,
): { t: number; distance: number } | undefined => {
  const [px, py] = point;
  const [x1, y1] = start;
  const [x2, y2] = end;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) return undefined;

  const t = clamp(((px - x1) * dx + (py - y1) * dy) / lengthSq, 0, 1);
  const projected: Point2D = [x1 + t * dx, y1 + t * dy];
  return { t, distance: getDistance(point, projected) };
};

/**
 * Choose the closest segment match within tolerance.
 * @param prevMatch
 * @param nextMatch
 * @param tolerance
 * @returns closest match or undefined
 */
const pickClosestSegmentMatch = (
  prevMatch: { t: number; distance: number } | undefined,
  nextMatch: { t: number; distance: number } | undefined,
  tolerance: number,
): SegmentMatch | undefined => {
  const prevOk = prevMatch !== undefined && prevMatch.distance <= tolerance;
  const nextOk = nextMatch !== undefined && nextMatch.distance <= tolerance;
  if (!prevOk && !nextOk) return undefined;
  if (prevOk && nextOk && prevMatch && nextMatch) {
    return prevMatch.distance <= nextMatch.distance
      ? { ...prevMatch, segment: 'prev' }
      : { ...nextMatch, segment: 'next' };
  }
  if (prevOk && prevMatch) {
    return { ...prevMatch, segment: 'prev' };
  }
  if (nextOk && nextMatch) {
    return { ...nextMatch, segment: 'next' };
  }
  return undefined;
};

/**
 * Interpolate a point on a segment using parameter t.
 * @param start
 * @param end
 * @param t
 * @returns interpolated point
 */
const interpolatePointOnSegment = (
  start: Point2D,
  end: Point2D,
  t: number,
): Point2D => {
  return [
    start[0] + (end[0] - start[0]) * t,
    start[1] + (end[1] - start[1]) * t,
  ];
};

/**
 * Check if two storey shape collections match by id and point positions.
 * @param left
 * @param right
 * @returns true if shapes are equivalent
 */
const areStoreyShapesEqual = (
  left: Storey['shapes'],
  right: Storey['shapes'],
): boolean => {
  if (left.length !== right.length) return false;
  return left.every((shape, index) => {
    const candidate = right[index];
    if (!candidate || shape.id !== candidate.id) return false;
    if ((shape.points?.length ?? 0) !== (candidate.points?.length ?? 0))
      return false;
    return shape.points.every((point, pointIndex) => {
      const candidatePoint = candidate.points[pointIndex];
      if (!candidatePoint) return false;
      return isEqualPoints(point, candidatePoint);
    });
  });
};
